import { queryOptions } from "@tanstack/react-query";
import { gql } from "graphql-request";

import { gqlClient } from "../../../shared/lib/gql-client";
import { IMAGE_FIELDS } from "../../images/api/fragments";
import { LINK_FIELDS } from "../../links/api/fragments";
import type { EventSeriesDetail } from "../types";

/**
 * The series itself, as opposed to one of its occurrences.
 *
 * Note what `images` and `links` mean here: the series' **own** rows, with no
 * override resolution in front of them. On `Event` those same field names return
 * the occurrence's rows if it has any and the series' otherwise, which is a
 * genuinely different thing. This is the set an occurrence falls back to.
 */
const SERIES_FIELDS = gql`
  ${LINK_FIELDS}
  ${IMAGE_FIELDS}
  fragment SeriesDetailFields on EventSeries {
    id
    title
    rrule
    startsAtFirst
    until
    durationMinutes
    isMine
    isCancelled
    # Copied onto every generated occurrence rather than resolved through the
    # series at read time, so this is the template's set, not necessarily what
    # any given night now shows.
    organisers {
      role
      organiser {
        id
        name
      }
    }
    images {
      ...ImageFields
    }
    links {
      ...LinkFields
    }
  }
`;

const EVENT_SERIES = gql`
  ${SERIES_FIELDS}
  query EventSeries($id: ID!) {
    eventSeries(id: $id) {
      ...SeriesDetailFields
    }
  }
`;

export const seriesQueries = {
  all: () => ["series"] as const,
  details: () => [...seriesQueries.all(), "detail"] as const,
  detail: (id: string) =>
    queryOptions({
      queryKey: [...seriesQueries.details(), id] as const,
      queryFn: () =>
        gqlClient.request<{ eventSeries: EventSeriesDetail | null }>(
          EVENT_SERIES,
          { id },
        ),
    }),
};
